import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  addDays,
  fmtHM,
  fmtISO,
  fmtLong,
  isSameDay,
  roundUpTo15,
  snapTo15,
} from '../lib/format.js';
import { LOG_WINDOW_DAYS } from '../lib/reviewWindow.js';
import { HoursInput } from './HoursInput.jsx';
import { Icon } from './Icon.jsx';
import { JiraSearch, JiraSearchHint } from './JiraSearch.jsx';
import { MinutesSegment } from './MinutesSegment.jsx';
import { Select } from './Select.jsx';
import { ClientSelect } from './ClientSelect.jsx';

const TARGET_MIN = 8 * 60;

/**
 * v2.1 persistence rules — split a JiraSearch selection into separate
 * ticketId/epicId fields. Extracted so the rules are unit-testable.
 *
 *   epic pick         → epicId/Title set, ticketId null
 *   ticket + parent   → ticketId/Title set, epicId/Title from parent
 *   standalone ticket → ticketId/Title set, epicId null
 *
 * When the activity hides the epic field (showEpic=false), preserve
 * whatever was already on disk (admin-policy: don't silently strip data
 * when activity rules change).
 */
export function buildJiraOutputs(jiraItem, showEpic, initial) {
  if (!showEpic) {
    return {
      ticketId: initial?.ticketId || '',
      ticketTitle: initial?.ticketTitle || '',
      epicId: initial?.epicId || '',
      epicTitle: initial?.epicTitle || '',
    };
  }
  if (jiraItem?.kind === 'ticket') {
    return {
      ticketId: jiraItem.id || '',
      ticketTitle: jiraItem.title || '',
      epicId: jiraItem.parentEpicId || '',
      epicTitle: jiraItem.parentEpicTitle || '',
    };
  }
  if (jiraItem?.kind === 'epic') {
    return {
      ticketId: '',
      ticketTitle: '',
      epicId: jiraItem.id || '',
      epicTitle: jiraItem.title || '',
    };
  }
  return { ticketId: '', ticketTitle: '', epicId: '', epicTitle: '' };
}

/**
 * Dedicated full-screen "Log time" surface — peer to Today and the
 * Calendar Import screen. Replaces the older modal layout.
 *
 * Layout:
 *  - Sticky header (mint clock, eyebrow, date + live total, Close)
 *  - Centered body (max 760px): Date+Time row, Activity, Product (if
 *    applicable), Client+Epic row (each conditional), Description
 *  - Sticky footer (Create another · ⌘+Enter / Esc hint · Cancel · Save)
 *
 * Conditional fields are driven by the activity's `fieldRules` returned
 * from /api/dictionary (or the per-field defaults when blank). The
 * mental model is the same as the design's `fields: { product, client,
 * epic }` boolean — visible when the rule is not 'hidden'.
 */
export function LogTimeForm({
  initial,
  user,
  mode = 'create',
  dictionary,
  onSave,
  onCancel,
  prefill,
  saving = false,
  defaultDate,
  prefillFromEvent,
}) {
  const isEdit = mode === 'edit';
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  // One-shot calendar-event prefill (date + duration + title as desc).
  const eventSeed = useMemo(() => {
    if (!prefillFromEvent) return null;
    const startIso = prefillFromEvent.start;
    const endIso = prefillFromEvent.end;
    let h = 0;
    let m = 0;
    if (startIso && endIso) {
      const diffMs =
        new Date(endIso).getTime() - new Date(startIso).getTime();
      // v2.0: round event duration UP to the next 15-min step before
      // populating the form. 23 min → 30 min, 46 min → 1h 00m. Cap at 8h.
      const rawMins = Math.max(0, Math.round(diffMs / 60000));
      const mins = Math.min(8 * 60, roundUpTo15(rawMins));
      h = Math.floor(mins / 60);
      m = mins % 60;
    }
    const date = startIso ? fmtISO(new Date(startIso)) : null;
    return {
      date,
      hours: h,
      minutes: m,
      description: (prefillFromEvent.title || '').slice(0, 200),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const seed = initial || {
    date: eventSeed?.date || fmtISO(defaultDate || today),
    activityName: prefill?.activityName || '',
    // Product is intentionally NOT carried over from the previous entry.
    // A pre-selected product reads as "already valid" and used to slip
    // through unchanged into a follow-up entry that needed a different
    // one — the reported hazard. Product is now always a deliberate pick
    // (empty + `required` forces the choice). Activity still pre-fills.
    product: '',
    clientName: '',
    ticketId: '',
    ticketTitle: '',
    epicId: '',
    epicTitle: '',
    hours: eventSeed?.hours ?? 0,
    minutes: eventSeed?.minutes ?? 0,
    description: eventSeed?.description || '',
  };

  const [date, setDate] = useState(seed.date);
  const [activity, setActivity] = useState(seed.activityName || '');
  const [product, setProduct] = useState(seed.product || '');
  const [client, setClient] = useState(seed.clientName || null);
  // jiraItem holds the last search selection:
  //   { kind: 'epic'|'ticket', id, title, parentEpicId, parentEpicTitle }
  // For existing (v2.0) entries, reconstruct a minimal item so the hint renders.
  const seedJiraItem = seed.ticketId
    ? { kind: 'ticket', id: seed.ticketId, title: seed.ticketTitle, parentEpicId: seed.epicId || null, parentEpicTitle: seed.epicTitle || null }
    : seed.epicId
    ? { kind: 'epic', id: seed.epicId, title: seed.epicTitle, parentEpicId: null, parentEpicTitle: null }
    : null;
  const [jiraItem, setJiraItem] = useState(seedJiraItem);
  const [hours, setHours] = useState(seed.hours || 0);
  // v2.0: snap incoming minutes to the 15-min grid. Legacy entries on disk
  // (created before v2.0) can have minutes like 22; we display the snapped
  // value as the new baseline and persist it on save. See AC-GRAN-03.
  const [minutes, setMinutes] = useState(snapTo15(seed.minutes || 0));
  const [desc, setDesc] = useState(seed.description || '');
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [createAnother, setCreateAnother] = useState(false);
  // Surface a transient banner after a Create-another save so the user
  // sees confirmation without losing the open form.
  const [lastSaved, setLastSaved] = useState(null);
  const lastSavedTimer = useRef(null);
  const lastSubmitRef = useRef(null);
  const continuedRef = useRef(false);
  const prevSavingRef = useRef(saving);
  const activityRef = useRef(null);

  const dept = user.department;
  const activityOptions = dictionary?.activitiesByDept?.[dept] || [];
  const activityMeta = dictionary?.activityMeta || {};
  const products = dictionary?.products || ['SwiftPay', 'SwiftGuard', 'None'];
  const clients = dictionary?.clients || [];
  // Per-field defaults — server returns these so the client doesn't have
  // to hardcode the fallback. Blank Client/Epic now mean "hidden" by
  // default (matches the v2 design semantic where tag fields opt-in per
  // activity).
  const defaultFieldRules = dictionary?.defaultFieldRules || {
    product: 'required',
    client: 'hidden',
    epic: 'hidden',
  };
  const descFor = (name) =>
    activityMeta[`${dept}::${name}`]?.description || '';
  // No activity picked yet → show Product (required by default) and hide
  // Client/Epic (default = hidden). Matches the design's "all-shown
  // until proven otherwise" only for Product.
  const fieldRules = activity
    ? activityMeta[`${dept}::${activity}`]?.fieldRules || defaultFieldRules
    : { product: 'required', client: 'hidden', epic: 'hidden' };
  const showProduct = fieldRules.product !== 'hidden';
  const showClient = fieldRules.client !== 'hidden';
  const showEpic = fieldRules.epic !== 'hidden';
  const productRequired = fieldRules.product === 'required';
  const clientRequired = fieldRules.client === 'required';
  const epicRequired = fieldRules.epic === 'required';

  // When activity changes, clear values for fields that the new activity
  // hides — keeps submitted payloads clean and the UI honest.
  useEffect(() => {
    if (!activity) return;
    if (!showProduct && product) setProduct('');
    if (!showClient && client) setClient(null);
    if (!showEpic && jiraItem) setJiraItem(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity]);

  // After a Create-another save the parent flips `saving` back to false
  // without closing the form. Detect that transition and reset state
  // for the next entry; the date sticks.
  useEffect(() => {
    if (prevSavingRef.current && !saving && continuedRef.current) {
      continuedRef.current = false;
      if (lastSubmitRef.current) {
        setLastSaved({
          mins: lastSubmitRef.current.mins,
          activity: lastSubmitRef.current.activity,
          when: Date.now(),
        });
        clearTimeout(lastSavedTimer.current);
        lastSavedTimer.current = setTimeout(
          () => setLastSaved(null),
          4500,
        );
      }
      setActivity(prefill?.activityName || '');
      // Do not carry the product to the next entry — see the seed comment.
      setProduct('');
      setClient(null);
      setJiraItem(null);
      setHours(0);
      setMinutes(0);
      setDesc('');
      setErrors({});
      setSubmitted(false);
    }
    prevSavingRef.current = saving;
  }, [saving]);

  useEffect(() => () => clearTimeout(lastSavedTimer.current), []);

  const totalMins =
    (parseInt(hours, 10) || 0) * 60 + (parseInt(minutes, 10) || 0);

  const dateOptions = useMemo(
    () => Array.from({ length: LOG_WINDOW_DAYS }, (_, i) => addDays(today, -i)),
    [today],
  );

  const charCount = desc.length;
  const charDanger = charCount >= 180;

  const validate = () => {
    const e = {};
    if (!activity) e.activity = 'Pick an activity';
    if (showProduct && productRequired && !product)
      e.product = 'Pick a product';
    if (showClient && clientRequired && !client) e.client = 'Pick a client';
    if (showEpic && epicRequired && !jiraItem?.id) e.epic = 'Pick a Jira epic or ticket';
    if (!totalMins || totalMins > TARGET_MIN) {
      e.time = !totalMins
        ? 'Time spent must be greater than zero'
        : 'Time spent must be 8h or less';
    }
    // v2.0 — minutes constrained to 0/15/30/45. The segmented control
    // can't produce other values, but a stale field state (or programmatic
    // tweak in the browser) might — surface it as the same `time` error.
    if (![0, 15, 30, 45].includes(parseInt(minutes, 10))) {
      e.time = 'Minutes must be 0, 15, 30, or 45';
    }
    if (hours < 0 || hours > 8) e.time = 'Hours must be 0–8';
    return e;
  };

  const handleSubmit = async () => {
    if (saving) return;
    const e = validate();
    setErrors(e);
    setSubmitted(true);
    if (Object.keys(e).length) return;
    // Preserve legacy values on disk for fields the current activity hides
    // (admin-policy: don't silently strip data when rules change).
    const productOut = showProduct ? product : initial?.product || '';
    const clientOut = showClient ? client || '' : initial?.clientName || '';
    const jiraOut = buildJiraOutputs(jiraItem, showEpic, initial);
    continuedRef.current = createAnother && !isEdit;
    if (continuedRef.current) {
      lastSubmitRef.current = { mins: totalMins, activity };
    }
    try {
      await onSave(
        {
          date,
          activityName: activity,
          product: productOut,
          clientName: clientOut,
          ticketId: jiraOut.ticketId,
          ticketTitle: jiraOut.ticketTitle,
          epicId: jiraOut.epicId,
          epicTitle: jiraOut.epicTitle,
          hours: parseInt(hours, 10) || 0,
          minutes: parseInt(minutes, 10) || 0,
          description: desc.trim(),
        },
        { andContinue: continuedRef.current },
      );
    } catch {
      // Save failed — don't let the post-save reset useEffect clear
      // the form. The parent already surfaced a toast.
      continuedRef.current = false;
    }
  };

  // Global ⌘+Enter / Esc — same chord whether focus is in an input or
  // outside it. App.jsx-level shortcuts (N, ←/→, T, ?) are suppressed
  // while the form is open so they don't fight.
  useEffect(() => {
    const onKey = (ev) => {
      if (ev.key === 'Escape' && !saving) onCancel();
      if ((ev.metaKey || ev.ctrlKey) && ev.key === 'Enter') handleSubmit();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity, product, hours, minutes, desc, client, jiraItem, date, saving]);

  // Autofocus Activity on mount — the most-frequent first interaction.
  useEffect(() => {
    const t = setTimeout(() => {
      const el = activityRef.current?.querySelector('button, input');
      if (el && !activity) el.focus();
    }, 80);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedDateObj = useMemo(() => {
    const [y, m, d] = date.split('-').map(Number);
    return new Date(y, m - 1, d);
  }, [date]);

  return (
    <div
      data-screen-label="Log time"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        background: 'var(--sp-bg-page)',
        display: 'flex',
        flexDirection: 'column',
        animation: 'tt-fade-in 200ms ease',
      }}
    >
      {/* Top bar */}
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          padding: '16px 28px',
          borderBottom: '1px solid var(--sp-border)',
          background: '#fff',
          flex: 'none',
        }}
      >
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
          <span
            style={{
              width: 32,
              height: 32,
              borderRadius: 10,
              background: 'var(--sp-mint-100)',
              color: '#007C7C',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon name="clock" size={16} />
          </span>
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: 'var(--sp-fg-3)',
              }}
            >
              {isEdit ? 'Edit entry' : 'Log time'}
            </span>
            <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--sp-fg-1)' }}>
              {fmtLong(selectedDateObj)}
              {totalMins > 0 && (
                <span style={{ color: 'var(--sp-fg-3)', fontWeight: 500 }}>
                  {' '}
                  · {fmtHM(totalMins)}
                </span>
              )}
            </span>
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <button className="btn btn-ghost" onClick={onCancel} disabled={saving}>
          <Icon name="x" size={14} /> Close
        </button>
      </header>

      {/* Body */}
      <div
        aria-busy={saving}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '28px 28px 32px',
          pointerEvents: saving ? 'none' : 'auto',
          opacity: saving ? 0.55 : 1,
          transition: 'opacity .15s ease',
          userSelect: saving ? 'none' : 'auto',
        }}
      >
        <div
          style={{
            maxWidth: 760,
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 22,
          }}
        >
          {/* Create-another success banner */}
          {lastSaved && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 12px 10px 14px',
                borderRadius: 10,
                background: 'var(--sp-mint-100)',
                color: '#026153',
                fontSize: 13,
                animation: 'tt-fade-in 220ms ease',
              }}
            >
              <span
                style={{
                  display: 'inline-flex',
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  background: '#05E6C5',
                  color: '#013F35',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Icon name="check" size={13} stroke={2.8} />
              </span>
              <span style={{ flex: 1 }}>
                Saved <strong>{lastSaved.activity}</strong> ·{' '}
                <strong>{fmtHM(lastSaved.mins)}</strong>. The form is ready
                for your next entry.
              </span>
              <button
                onClick={() => setLastSaved(null)}
                className="btn btn-ghost btn-icon"
                style={{ width: 24, height: 24, color: '#026153' }}
                title="Dismiss"
              >
                <Icon name="x" size={12} />
              </button>
            </div>
          )}

          {/* Row 1: Date + Time spent */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1.4fr 1fr',
              gap: 14,
            }}
          >
            <div>
              <label className="tt-field-label">Date</label>
              <Select
                value={date}
                options={dateOptions.map((d) => ({
                  key: fmtISO(d),
                  label: fmtLong(d),
                  date: d,
                }))}
                getKey={(o) => o.key}
                getLabel={(o) => o.label}
                getMeta={(o) =>
                  isSameDay(o.date, today)
                    ? 'Today'
                    : isSameDay(o.date, addDays(today, -1))
                    ? 'Yesterday'
                    : ''
                }
                onChange={setDate}
                placeholder="Pick a date"
              />
            </div>
            <div>
              <label className="tt-field-label">
                Time spent <span className="tt-field-required">*</span>
              </label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
                <HoursInput
                  value={hours}
                  onChange={setHours}
                  error={submitted && !!errors.time}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <MinutesSegment
                    value={minutes}
                    onChange={setMinutes}
                    error={submitted && !!errors.time}
                  />
                </div>
              </div>
            </div>
          </div>
          {submitted && errors.time && (
            <div className="tt-error-msg" style={{ marginTop: -14 }}>
              <Icon name="alert" size={12} /> {errors.time}
            </div>
          )}
          {!errors.time && totalMins > 0 && (
            <div
              className="tt-help"
              style={{ marginTop: -14, color: 'var(--sp-fg-2)' }}
            >
              Logging <strong>{fmtHM(totalMins)}</strong> on{' '}
              <strong>{fmtLong(selectedDateObj)}</strong>
            </div>
          )}

          {/* Activity */}
          <div ref={activityRef}>
            <label className="tt-field-label">
              Activity <span className="tt-field-required">*</span>
            </label>
            <Select
              value={activity}
              options={activityOptions.map((a) => ({
                key: a,
                label: a,
                desc: descFor(a),
              }))}
              getKey={(o) => o.key}
              getLabel={(o) => o.label}
              getDescription={(o) => o.desc}
              placeholder={`Pick an activity in ${dept}`}
              onChange={setActivity}
              error={submitted && !!errors.activity}
              searchable
              searchPlaceholder="Search activities…"
              searchNoun="activity"
            />
            {submitted && errors.activity && (
              <div className="tt-error-msg">
                <Icon name="alert" size={12} /> {errors.activity}
              </div>
            )}
            {!errors.activity && activity && descFor(activity) && (
              <div
                className="tt-help"
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 6,
                  color: 'var(--sp-fg-2)',
                }}
              >
                <span
                  style={{
                    color: 'var(--sp-fg-3)',
                    display: 'inline-flex',
                    marginTop: 1,
                    flexShrink: 0,
                  }}
                >
                  <Icon name="info" size={12} />
                </span>
                <span>{descFor(activity)}</span>
              </div>
            )}
          </div>

          {/* Product — visibility depends on activity */}
          {showProduct && (
            <div>
              <label className="tt-field-label">
                Product
                {productRequired && (
                  <span className="tt-field-required">*</span>
                )}
              </label>
              <div
                className="tt-segmented"
                style={{
                  '--cols': products.length,
                  ...(submitted && errors.product
                    ? { boxShadow: '0 0 0 1px #B30745 inset' }
                    : {}),
                }}
              >
                {products.map((p) => (
                  <button
                    key={p}
                    type="button"
                    aria-pressed={product === p}
                    onClick={() => setProduct(p)}
                  >
                    {p === 'SwiftPay' && (
                      <span
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          background: '#05E6C5',
                          display: 'inline-block',
                        }}
                      />
                    )}
                    {p === 'SwiftGuard' && (
                      <span
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          background: '#FFA672',
                          display: 'inline-block',
                        }}
                      />
                    )}
                    {p === 'None' && (
                      <span
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          background: 'var(--sp-fg-4)',
                          display: 'inline-block',
                        }}
                      />
                    )}
                    {p}
                  </button>
                ))}
              </div>
              {submitted && errors.product && (
                <div className="tt-error-msg">
                  <Icon name="alert" size={12} /> {errors.product}
                </div>
              )}
            </div>
          )}

          {/* Client + Epic side-by-side when both apply, else single column */}
          {(showClient || showEpic) && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns:
                  showClient && showEpic ? '1fr 1fr' : '1fr',
                gap: 14,
              }}
            >
              {showClient && (
                <div>
                  <label className="tt-field-label">
                    Client
                    {clientRequired && (
                      <span className="tt-field-required">*</span>
                    )}
                  </label>
                  <ClientSelect
                    value={client}
                    clients={clients}
                    placeholder={clientRequired ? 'Pick a client' : 'No client'}
                    emptyLabel={clientRequired ? null : 'No client'}
                    onChange={setClient}
                    error={submitted && !!errors.client}
                    searchPlaceholder="Search clients…"
                  />
                  {submitted && errors.client && (
                    <div className="tt-error-msg">
                      <Icon name="alert" size={12} /> {errors.client}
                    </div>
                  )}
                </div>
              )}
              {showEpic && (
                <div>
                  <label className="tt-field-label">
                    Jira epic or ticket
                    {epicRequired && (
                      <span className="tt-field-required">*</span>
                    )}
                  </label>
                  <JiraSearch
                    value={jiraItem?.id}
                    valueTitle={jiraItem?.title}
                    valueKind={jiraItem?.kind}
                    onChange={setJiraItem}
                  />
                  <JiraSearchHint item={jiraItem} />
                  {submitted && errors.epic && (
                    <div className="tt-error-msg">
                      <Icon name="alert" size={12} /> {errors.epic}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Description */}
          <div>
            <label
              className="tt-field-label"
              style={{ display: 'flex', justifyContent: 'space-between' }}
            >
              <span>Description</span>
              <span
                style={{
                  fontWeight: 500,
                  fontVariantNumeric: 'tabular-nums',
                  color: charDanger ? '#B30745' : 'var(--sp-fg-3)',
                }}
              >
                {charCount} / 200
              </span>
            </label>
            <textarea
              className="tt-input"
              value={desc}
              onChange={(e) => setDesc(e.target.value.slice(0, 200))}
              placeholder="What did you work on? Optional, but helps your future self."
              maxLength={200}
              rows={3}
            />
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer
        style={{
          padding: '14px 28px',
          borderTop: '1px solid var(--sp-border)',
          background: '#fff',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          flex: 'none',
        }}
      >
        {!isEdit ? (
          <label
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 13,
              color: 'var(--sp-fg-2)',
              cursor: 'pointer',
              userSelect: 'none',
            }}
          >
            <input
              type="checkbox"
              checked={createAnother}
              onChange={(e) => setCreateAnother(e.target.checked)}
              style={{
                width: 15,
                height: 15,
                accentColor: 'var(--sp-mint-500, #05E6C5)',
                cursor: 'pointer',
              }}
            />
            <span>Create another</span>
          </label>
        ) : (
          <div />
        )}
        <div
          style={{
            flex: 1,
            fontSize: 12,
            color: 'var(--sp-fg-3)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            justifyContent: 'center',
          }}
        >
          <kbd
            style={{
              background: 'var(--sp-bg-muted)',
              border: '1px solid var(--sp-border)',
              borderRadius: 4,
              padding: '1px 6px',
              fontFamily: 'inherit',
              fontSize: 11,
              fontWeight: 600,
            }}
          >
            ⌘
          </kbd>
          <span>+</span>
          <kbd
            style={{
              background: 'var(--sp-bg-muted)',
              border: '1px solid var(--sp-border)',
              borderRadius: 4,
              padding: '1px 6px',
              fontFamily: 'inherit',
              fontSize: 11,
              fontWeight: 600,
            }}
          >
            Enter
          </kbd>
          <span>to save</span>
          <span style={{ color: 'var(--sp-fg-4)', margin: '0 4px' }}>·</span>
          <kbd
            style={{
              background: 'var(--sp-bg-muted)',
              border: '1px solid var(--sp-border)',
              borderRadius: 4,
              padding: '1px 6px',
              fontFamily: 'inherit',
              fontSize: 11,
              fontWeight: 600,
            }}
          >
            Esc
          </kbd>
          <span>to close</span>
        </div>
        <button className="btn btn-ghost" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        <button
          className="btn btn-primary"
          onClick={handleSubmit}
          disabled={saving}
          style={{ whiteSpace: 'nowrap' }}
        >
          {saving && (
            <span className="tt-spinner sm" style={{ marginRight: 6 }} />
          )}
          {saving
            ? 'Saving…'
            : isEdit
            ? 'Save changes'
            : createAnother
            ? 'Save & add another'
            : 'Save entry'}
        </button>
      </footer>
    </div>
  );
}
